import { useState } from 'react';
import { ENTI, TIPI, FORMATS } from './data/atti';

export interface ScelteAtto {
  ente: string;
  tipo: string;
  format: string;
  /** true quando si è partiti da un documento caricato */
  daFile: boolean;
  nomeFile?: string;
}

/**
 * «DA DOVE PARTIAMO» — la schermata di ingresso dell'ambiente immersivo.
 *
 * Perché esiste: gli atti sono disciplinati da ciascun ente con regole proprie.
 * Non si può aprire un editor vuoto e sperare: prima si sa PER CHI si scrive
 * (l'ente determina intestazione, formula del visto, ordine degli allegati),
 * poi CHE COSA (la tipologia determina struttura, controlli e passi della
 * guida), poi CON QUALE FORMAT.
 *
 * L'alternativa è partire da un documento già scritto: si carica e l'assistente
 * ne riconosce la struttura. Il file resta dell'utente, NON entra nel corpus.
 */
export function AvvioAtto({ onApri }: { onApri: (s: ScelteAtto, scheda?: 'guida' | 'skill') => void }) {
  const [via, setVia] = useState<'file' | 'format'>('format');
  const [ente, setEnte] = useState(ENTI[1]!.id);
  const [tipo, setTipo] = useState('decreto');
  const [format, setFormat] = useState(FORMATS.decreto![0]!.id);
  const [file, setFile] = useState<string | null>(null);
  const [sopra, setSopra] = useState(false);

  const formats = FORMATS[tipo] ?? FORMATS.decreto!;
  const enteAttivo = ENTI.find((e) => e.id === ente) ?? ENTI[0]!;
  const tipoAttivo = TIPI.find((t) => t.id === tipo) ?? TIPI[0]!;
  const nFormat = Object.values(FORMATS).reduce((t, l) => t + l.length, 0);

  function scegliTipo(id: string) {
    setTipo(id);
    setFormat((FORMATS[id] ?? FORMATS.decreto!)[0]!.id);   // il format deve seguire il tipo
  }

  const scelte = (): ScelteAtto => ({
    ente, tipo, format, daFile: via === 'file',
    ...(file ? { nomeFile: file } : {}),
  });

  return (
    <div className="ui-avvio">
      <h1>Da dove partiamo</h1>
      <p className="ui-lede">
        Puoi riprendere un documento che hai già — Word o OnlyOffice, resta il tuo file — oppure partire da un
        format dell'ente. In entrambi i casi l'assistente ti accompagna passo per passo e tieni aperto il
        corpus normativo.
      </p>

      <div className="ui-vie">
        <button type="button" className="ui-via" aria-pressed={via === 'file'} onClick={() => setVia('file')}>
          <div className="ui-via-testa">
            <span className="ui-via-nome">Parti da un documento</span>
            <span className="ui-via-meta">
              {via === 'file' && <span className="ui-scelta">SCELTA</span>}
              <span className="ui-mono">.docx .odt .doc</span>
            </span>
          </div>
          <p>
            Carichi la bozza che hai già scritto. L'assistente ne riconosce la struttura, individua i richiami
            normativi e ti dice cosa manca. Il file resta modificabile in OnlyOffice.
          </p>
        </button>

        <button type="button" className="ui-via" aria-pressed={via === 'format'} onClick={() => setVia('format')}>
          <div className="ui-via-testa">
            <span className="ui-via-nome">Parti da un format</span>
            <span className="ui-via-meta">
              {via === 'format' && <span className="ui-scelta">SCELTA</span>}
              <span className="ui-mono">{nFormat} format</span>
            </span>
          </div>
          <p>
            Struttura, intestazioni e formule di chiusura già conformi alla prassi dell'ente. Scegli ente, tipo
            di atto e format: il resto lo scrivi tu, assistito.
          </p>
        </button>
      </div>

      {via === 'file' ? (
        <section className="ui-passo">
          <div
            className={sopra ? 'ui-drop sopra' : 'ui-drop'}
            onDragOver={(e) => { e.preventDefault(); setSopra(true); }}
            onDragLeave={() => setSopra(false)}
            onDrop={(e) => {
              e.preventDefault(); setSopra(false);
              const f = e.dataTransfer.files[0];
              if (f) setFile(f.name);   // TODO: POST /api/atti/importa
            }}
          >
            <div className="ui-drop-t">Trascina qui il documento</div>
            <div className="ui-drop-n">
              Word, OpenDocument, RTF. Anche una scansione: parte l'OCR.<br />
              Il documento non entra nel corpus: resta il tuo atto in lavorazione.
            </div>
            <label className="ui-btn primario">
              Scegli dal disco
              <input
                type="file"
                accept=".docx,.doc,.odt,.rtf,.pdf"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f.name); e.target.value = ''; }}
              />
            </label>
          </div>

          {file && (
            <div className="ui-file">
              <span className="ui-punto verde" aria-hidden="true" />
              <div>
                <div className="ui-file-nome">{file}</div>
                <div className="ui-file-meta">
                  Riconosciuto come <strong>Decreto di ammissione</strong> · 4 richiami normativi trovati ·
                  2 sezioni mancanti
                </div>
              </div>
              <button type="button" className="ui-btn primario" onClick={() => onApri(scelte())}>
                Apri nell'ambiente immersivo
              </button>
            </div>
          )}
        </section>
      ) : (
        <>
          <section className="ui-passo">
            <div className="ui-passo-t"><span className="ui-num">1</span><h2>Per quale ente scrivi</h2></div>
            <p className="ui-passo-n">
              I format cambiano da ente a ente: intestazione, formula del visto, ordine degli allegati,
              chiusura. L'elenco arriva dai tenant configurati sulla piattaforma.
            </p>
            <select className="ui-sel" value={ente} onChange={(e) => setEnte(e.target.value)}>
              {ENTI.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
            <p className="ui-nota">{enteAttivo.nota}</p>
          </section>

          <section className="ui-passo">
            <div className="ui-passo-t"><span className="ui-num">2</span><h2>Che atto devi produrre</h2></div>
            <p className="ui-passo-n">
              {TIPI.length} tipologie. Il tipo determina la struttura, i controlli di conformità e i passi della guida.
            </p>
            <div className="ui-tipi">
              {TIPI.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={t.id === tipo ? 'ui-tipo sel' : 'ui-tipo'}
                  aria-pressed={t.id === tipo}
                  onClick={() => scegliTipo(t.id)}
                >
                  <span className="ui-tipo-n">{t.nome}</span>
                  <span className="ui-tipo-x">{t.nota}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="ui-passo">
            <div className="ui-passo-t"><span className="ui-num">3</span><h2>Quale format — {tipoAttivo.nome}</h2></div>
            <p className="ui-passo-n">
              Un format è una struttura vuota conforme: non contiene testo dell'atto, solo l'ossatura e i segnaposto.
            </p>
            <div className="ui-formats">
              {formats.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={f.id === format ? 'ui-format sel' : 'ui-format'}
                  aria-pressed={f.id === format}
                  onClick={() => setFormat(f.id)}
                >
                  <div className="ui-format-testa">
                    <span className="ui-format-n">{f.nome}</span>
                    <span className="ui-mono">{f.versione}</span>
                  </div>
                  <div className="ui-format-x">{f.nota}</div>
                  <div className="ui-format-tag">{f.tag.map((g) => <span key={g}>{g}</span>)}</div>
                </button>
              ))}
            </div>
          </section>

          <div className="ui-fascia">
            <div>
              <div className="ui-fascia-t">Ambiente immersivo</div>
              <p>
                Guida passo per passo, proposte con la fonte a fianco, corpus normativo interrogabile senza
                uscire dall'atto e le skill di UranIA orchestrate in base al tipo di atto.
              </p>
            </div>
            <div className="ui-fascia-azioni">
              {/* Seconda porta: entra già sulla scheda delle skill. Serve a chi
                  sa di dover orchestrare, non a chi deve scrivere da zero. */}
              <button type="button" className="ui-btn fantasma" onClick={() => onApri(scelte(), 'skill')}>
                Apri e orchestra le skill
              </button>
              <button type="button" className="ui-btn chiaro" onClick={() => onApri(scelte(), 'guida')}>
                Apri nell'ambiente immersivo
              </button>
            </div>
          </div>
        </>
      )}

      <div className="ui-aiact">
        <span className="tag">AI ACT</span>
        <p>
          Il riconoscimento della struttura di un documento caricato e le proposte di testo sono prodotte da
          modelli linguistici ai sensi del Reg. UE 2024/1689 (art. 50) e della L. 132/2025: vanno verificate
          prima di assumere decisioni amministrative. <a href="/normativa">Normativa</a>
        </p>
      </div>
    </div>
  );
}
